import { MessageCircle } from "lucide-react";
import { loadImg, iconImg, roundRect, text, drawBackground, drawAvatar, drawBrand, drawCta, drawHeart, drawGrain } from "@/lib/badgeImage";

export const LAYOUTS = {
  story: { W: 1080, H: 1920, brandY: 150, top: 300, bottom: 1470, size: 48, cardY: 1590, cardH: 250, scale: 1.2 },
  square: { W: 1080, H: 1080, brandY: 82, top: 165, bottom: 830, size: 36, cardY: 878, cardH: 158, scale: 1 },
};

const wrap = (ctx, str, maxW, maxLines) => {
  const lines = [];
  for (const para of str.split("\n")) {
    let cur = "";
    for (const w of para.split(/\s+/).filter(Boolean)) {
      const next = cur ? `${cur} ${w}` : w;
      if (ctx.measureText(next).width <= maxW || !cur) { cur = next; continue; }
      lines.push(cur); cur = w;
    }
    if (cur) lines.push(cur);
  }
  if (lines.length <= maxLines) return lines;
  const out = lines.slice(0, maxLines);
  out[maxLines - 1] = `${out[maxLines - 1].replace(/\s+\S*$/, "")} …`;
  return out;
};

const drawCover = (ctx, img, x, y, w, h, r) => {
  const s = Math.max(w / img.width, h / img.height);
  const iw = img.width * s, ih = img.height * s;
  ctx.save(); roundRect(ctx, x, y, w, h, r); ctx.clip();
  ctx.drawImage(img, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih);
  ctx.restore();
};

const shade = (ctx, W, y0, y1, a0, a1) => {
  const g = ctx.createLinearGradient(0, y0, 0, y1);
  g.addColorStop(0, `rgba(30,6,24,${a0})`); g.addColorStop(1, `rgba(30,6,24,${a1})`);
  ctx.fillStyle = g; ctx.fillRect(0, y0, W, y1 - y0);
};

export const fitLines = (ctx, str, maxW, maxH, start, min, weight, font) => {
  let size = start, lines = [];
  for (; size >= min; size -= 4) {
    ctx.font = `${weight} ${size}px "${font}", sans-serif`;
    lines = wrap(ctx, str, maxW, Math.max(1, Math.floor(maxH / (size * 1.32))));
    if (!lines[lines.length - 1]?.endsWith(" …")) break;
  }
  return { size: Math.max(size, min), lines };
};

async function photoLayout(ctx, L, post, img) {
  const { W, H } = L, story = L.H > L.W, pad = 72;
  drawCover(ctx, img, 0, 0, W, H, 0);
  shade(ctx, W, 0, H * 0.24, 0.55, 0);
  shade(ctx, W, H * 0.38, H, 0, 0.88);
  drawGrain(ctx, W, H, 4000);
  await drawBrand(ctx, W, L.brandY, L.scale);
  const capBottom = L.cardY - (story ? 60 : 34);
  const { size, lines } = post.text ? fitLines(ctx, post.text, W - pad * 2, story ? 280 : 120, L.size + 6, L.size - 6, 700, "Plus Jakarta Sans") : { size: L.size, lines: [] };
  const lh = size * 1.32;
  let y = capBottom - lines.length * lh + size;
  ctx.save(); ctx.shadowColor = "rgba(0,0,0,0.5)"; ctx.shadowBlur = 16;
  lines.forEach((l) => { text(ctx, l, pad, y, { size, weight: 700, font: "Plus Jakarta Sans", align: "left", maxW: W - pad * 2 }); y += lh; });
  ctx.restore();
  const ar = story ? 46 : 34, ay = capBottom - lines.length * lh - ar - (story ? 34 : 22);
  const avatar = await loadImg(post.picture, true);
  drawAvatar(ctx, avatar, post.name, pad + ar, ay, ar);
  text(ctx, post.name, pad + ar * 2 + 24, ay - 2, { size: story ? 42 : 34, weight: 800, align: "left", maxW: W - pad * 2 - ar * 2 - 40 });
  text(ctx, "in der itzPlay Community", pad + ar * 2 + 24, ay + (story ? 38 : 30), { size: story ? 28 : 22, weight: 600, font: "Plus Jakarta Sans", align: "left", color: "rgba(255,255,255,0.8)" });
}

async function cardLayout(ctx, L, post) {
  const { W } = L, story = L.H > L.W, x = 72, w = W - 144, pad = story ? 64 : 48;
  drawBackground(ctx, W, L.H);
  await drawBrand(ctx, W, L.brandY, L.scale);
  ctx.save(); ctx.shadowColor = "rgba(131,24,67,0.4)"; ctx.shadowBlur = 70; ctx.shadowOffsetY = 26;
  roundRect(ctx, x, L.top, w, L.bottom - L.top, 56); ctx.fillStyle = "#fff"; ctx.fill(); ctx.restore();
  text(ctx, "”", x + w - pad - 10, L.top + (story ? 230 : 170), { size: story ? 300 : 220, weight: 800, color: "rgba(236,72,153,0.12)", align: "right" });
  const ar = story ? 52 : 38, hy = L.top + pad + ar;
  const [avatar, chat] = await Promise.all([loadImg(post.picture, true), iconImg(MessageCircle, "#64748B")]);
  drawAvatar(ctx, avatar, post.name, x + pad + ar, hy, ar);
  ctx.beginPath(); ctx.arc(x + pad + ar, hy, ar + 4, 0, Math.PI * 2); ctx.lineWidth = 4; ctx.strokeStyle = "#F9A8D4"; ctx.stroke();
  const tx = x + pad + ar * 2 + 26;
  text(ctx, post.name, tx, hy - 4, { size: story ? 42 : 32, weight: 800, color: "#1E1B1D", align: "left", maxW: w - pad * 2 - ar * 2 - 40 });
  text(ctx, "itzPlay Community", tx, hy + (story ? 38 : 30), { size: story ? 28 : 22, weight: 600, font: "Plus Jakarta Sans", color: "#E11D48", align: "left" });
  const footH = story ? 110 : 80, bodyTop = hy + ar + (story ? 50 : 34), bodyBot = L.bottom - footH - pad / 2;
  const { size, lines } = fitLines(ctx, post.text || "", w - pad * 2, bodyBot - bodyTop, story ? 84 : 60, L.size, 800, "Outfit");
  const lh = size * 1.32;
  let y = bodyTop + Math.max(0, (bodyBot - bodyTop - lines.length * lh) / 2) + size;
  lines.forEach((l) => { text(ctx, l, x + pad, y, { size, weight: 800, color: "#1E1B1D", align: "left", maxW: w - pad * 2 }); y += lh; });
  const fy = L.bottom - footH / 2 - pad / 3;
  ctx.fillStyle = "#FCE7F3"; ctx.fillRect(x + pad, L.bottom - footH - pad / 3, w - pad * 2, 2);
  const isz = story ? 44 : 34;
  drawHeart(ctx, x + pad + isz / 2, fy - isz * 0.55, isz, "#F43F5E");
  text(ctx, String(post.likes ?? 0), x + pad + isz + 14, fy + isz * 0.3, { size: story ? 36 : 28, weight: 800, color: "#1E1B1D", align: "left" });
  const cx = x + pad + isz + 120;
  if (chat) ctx.drawImage(chat, cx, fy - isz / 2, isz, isz);
  text(ctx, String(post.comments ?? 0), cx + isz + 14, fy + isz * 0.3, { size: story ? 36 : 28, weight: 800, color: "#1E1B1D", align: "left" });
  text(ctx, "Jetzt mitreden →", x + w - pad, fy + isz * 0.3, { size: story ? 32 : 26, weight: 800, color: "#E11D48", align: "right" });
  const bx = x + w - 30, by = L.top - 10, br = story ? 64 : 48;
  ctx.save(); ctx.shadowColor = "rgba(131,24,67,0.35)"; ctx.shadowBlur = 30;
  ctx.beginPath(); ctx.arc(bx, by, br, 0, Math.PI * 2); ctx.fillStyle = "#fff"; ctx.fill(); ctx.restore();
  drawHeart(ctx, bx, by - br * 0.5, br * 1.05, "#F43F5E");
}

export async function renderPostImage({ post, photo, code, format }) {
  await document.fonts?.ready;
  const L = LAYOUTS[format];
  const c = document.createElement("canvas");
  c.width = L.W; c.height = L.H;
  const ctx = c.getContext("2d");
  const img = await loadImg(photo, true);
  if (img) await photoLayout(ctx, L, post, img);
  else await cardLayout(ctx, L, post);
  await drawCta(ctx, L.W, L.cardY, L.cardH, { code });
  return new Promise((resolve) => c.toBlob((bl) => resolve(bl), "image/png"));
}
